"use client";

import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { AnimatePresence, motion } from "framer-motion";
import { AnimateChangeInHeight } from "@/components/animations/animateHeight";
import { CheckIcon, EnvelopeIcon } from "@heroicons/react/20/solid";
import { PhoneValidation } from "@/components/phoneValidation";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { getCurrentTime, useSearchEngineDetector } from "@/lib/utils";
import Bowser from "bowser";
import ToggleSwitch from "@/components/ui/toggleSwitch";
import { secureInput } from "@/lib/security";
import { emailTypos } from "@/lib/constants";

async function sendEmail<T>(data: T) {
	try {
		const siteUrl = window.location.origin;
		const response = await axios.post(`${siteUrl}/wp-json/contact-form/v1/send-enquiry`, data);
		return response;
	} catch (error) {
		console.error("There was an error sending the email:", error);
	}
}

function usePrevious(value: number) {
	let ref = useRef(value);

	useEffect(() => {
		ref.current = value;
	}, [value]);

	return ref.current;
}

const variants = {
	initial: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
	animate: {
		opacity: 1,
	},
	exit: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
};

type QuickFormProps = {
	className?: string;
	siteTitle?: string;
	rootId?: HTMLElement;
};

export function GeneralForm({ className, siteTitle, rootId }: QuickFormProps) {
	// Get values and setters from Zustand store
	const { formValues, setFormValue, setFormError, resetFormValues } = useFormDataStore();
	const {
		setValidateFormDetails,
		toggleTC,
		setToggleTC,
		isPhoneVerified,
		setPhoneVerified,
		appSettings,
		setAppSettings,
		isSubmitted,
		setIsSubmitted,
		selectedIndex,
		setSelectedIndex,
		setFormErrors,
		hasAttemptedSubmit,
		setHasAttemptedSubmit,
	} = useFormStateStore();

	const detectedSearchEngine = useSearchEngineDetector();
	const [searchEngine, setSearchEngine] = useState<string>("Direct");
	const [userIP, setUserIP] = useState("");
	const [usingVPN, setUsingVPN] = useState(false);
	const [browserInfo, setBrowserInfo] = useState({});
	const prevIndex = usePrevious(selectedIndex);
	const isForwards = prevIndex === undefined ? true : selectedIndex > prevIndex;
	const [stepsMaxIndex, setStepsMaxIndex] = useState(1);
	const sourceUrl = window.location.href;

	// Toggle Switch for T&C
	const [enabledTC, setEnabledTC] = useState(toggleTC);

	useEffect(() => {
		setToggleTC(enabledTC);
		if (errors.tc) {
			setErrors((prev) => ({ ...prev, tc: !enabledTC }));
		}
	}, [enabledTC]);

	const { firstName, lastName, email, phone, enquiry } = formValues;
	const [emailMessage, setEmailMessage] = useState("");

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		firstName: false,
		lastName: false,
		phone: false,
		email: false,
		enquiry: false,
		tc: false,
	});

	// Email Typo Validation Helper
	const getEmailValidationError = (emailValue: string): string => {
		const trimmed = emailValue.trim();
		if (trimmed === "") return "Email is required";

		const sanitizedValue = secureInput.email(trimmed);
		if (!sanitizedValue.isValid) return "Email is not correctly formatted";

		const fullValue = trimmed.toLowerCase();
		const parts = fullValue.split("@");

		if (parts.length === 2) {
			const [username, domain] = parts;
			const typoRegex = /\.(con\.au|cm\.au|con|cm|co)$/i;
			const endTypoRegex = /\.com[a-z]$/;

			if (emailTypos[domain]) {
				return `Did you mean ${username}@${emailTypos[domain]}?`;
			} else if (typoRegex.test(domain)) {
				const fixedDomain = domain.replace(typoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			} else if (endTypoRegex.test(domain)) {
				const fixedDomain = domain.replace(endTypoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			}
		}
		return "";
	};

	// Handle input changes with validation
	const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.name(value);
		setFormValue("firstName", sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, firstName: !sanitizedValue.isValid }));
	};

	const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.name(value);
		setFormValue("lastName", sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, lastName: !sanitizedValue.isValid }));
	};

	const handleEmailChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.email(value);
			setFormValue("email", sanitizedValue.sanitized);

			const emailErrorMsg = getEmailValidationError(sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, email: emailErrorMsg !== "" }));
			setEmailMessage(emailErrorMsg);
		},
		[setFormValue],
	);

	const handleEnquiryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.textarea(value);
		setFormValue("enquiry", sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, enquiry: !sanitizedValue.isValid }));
	};

	// Function to validate all fields
	const validateForm = () => {
		const emailErrorMsg = getEmailValidationError(email);

		const newErrors = {
			firstName: !secureInput.name(firstName).isValid,
			lastName: !secureInput.name(lastName).isValid,
			email: emailErrorMsg !== "",
			phone: !secureInput.phone(phone).isValid,
			enquiry: !secureInput.textarea(enquiry).isValid,
			tc: !enabledTC,
		};

		if (phone.trim() === "") {
			setFormError("phone", "Phone number is required");
		}

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [firstName, lastName, email, phone, enquiry, enabledTC]);

	// Apply Color Theme Settings
	useEffect(() => {
		if (
			siteTitle?.includes("Contact") ||
			siteTitle?.includes("Compare") ||
			siteTitle?.includes("Fast Quote") ||
			siteTitle?.includes("Agri")
		) {
			setAppSettings("Jade Finance");
		} else {
			setAppSettings(siteTitle ?? "Jade Finance");
		}

		if (!rootId) return;

		rootId?.style.setProperty(`--ct-primary`, appSettings.themeColor);
		rootId?.style.setProperty(`--ct-secondary`, appSettings.themeHeaderTxt);
		rootId?.style.setProperty(`--ct-primary-darker`, appSettings.hoverColor);
		rootId?.style.setProperty(`--ct-bullet-bg`, appSettings.bulletBG);
	}, [appSettings]);

	const scrollToHeading = () => {
		if (!rootId) return;

		if (window.matchMedia("(max-width: 767px)").matches) {
			setTimeout(() => {
				const additionalOffset = 0; // adjust if you want some spacing above the form
				const targetPosition =
					rootId.getBoundingClientRect().top + window.scrollY - additionalOffset;

				window.scrollTo({
					top: targetPosition,
					behavior: "smooth",
				});
			}, 100);
		}
	};

	useEffect(() => {
		setSearchEngine(detectedSearchEngine);
	}, [detectedSearchEngine]);

	useEffect(() => {
		const browser = Bowser.parse(window.navigator.userAgent);
		const info = `${browser.browser.name} ${browser.browser.version}, ${browser.os.name}, ${browser.platform.type}`;
		setBrowserInfo(info);
	}, []);

	window.leadGenerated = window.leadGenerated || false;

	const loadDataLayerScript = () => {
		if (!window.leadGenerated) {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: "generate_lead",
				enhanced_conversion_data: {
					email: email,
					phone_number: "+61" + phone?.substring(1),
					first_name: firstName,
					last_name: lastName,
				},
			});
			window.leadGenerated = true;
		}
	};

	const getUserIP = useCallback(async () => {
		const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/getIP`);
		const data = await response.data;
		setUserIP(data.ip);
		setUsingVPN(data.vpn);
	}, []);

	const sendMail = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const zapPost = {
				...formValues,
				dateSubmitted: getCurrentTime(),
				siteTitle: `${siteTitle} Contact Form`,
				formName: `${siteTitle} General Contact Form Submission`,
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
			};
			const data = await sendEmail(zapPost);
			return data;
		},
		onSuccess() {
			resetFormValues();
			setEnabledTC(false);
		},
	});

	// Prevents Next button from being spammed causing skipping multiple pages.
	const [isPageLoading, setIsPageLoading] = useState(false);

	useEffect(() => {
		if (hasAttemptedSubmit && !isPhoneVerified && phone.trim() !== "") {
			setFormError("phone", "Please verify your phone number.");
		}
	}, [hasAttemptedSubmit, isPhoneVerified]);

	function submit() {
		setHasAttemptedSubmit(true);
		if (isPageLoading) return;

		scrollToHeading();

		const isValid = validateForm();

		if (hasAttemptedSubmit && !isPhoneVerified && phone.trim() !== "") {
			setFormError("phone", "Please verify your phone number.");
		}

		if (!isValid) return;

		if (isPhoneVerified && enquiry !== "") {
			sendMail.mutate();
			setToggleTC(false);
			setIsSubmitted(true);
			loadDataLayerScript();
			setFormError("phone", "");
			setSelectedIndex(stepsMaxIndex);
		}
	}

	function close() {
		scrollToHeading();
		setSelectedIndex(0);
		setToggleTC(false);
		setPhoneVerified(false);
		if (isSubmitted) {
			setIsSubmitted(false);
			setHasAttemptedSubmit(false);
		}
	}

	useEffect(() => {
		if (isPhoneVerified) {
			getUserIP();
		}
	}, [isPhoneVerified]);

	const handleKeyDown = useCallback((event: any) => {
		if (event.key === "Enter") {
			event.preventDefault();
		}
	}, []);

	// Reset form errors on focus
	const handleFocus = (field: keyof typeof errors) => {
		setErrors((prev) => ({ ...prev, [field]: false }));
	};

	return (
		<div className={clsx(className, "")}>
			{/* Form */}
			<AnimateChangeInHeight>
				<form
					className="jF-relative jF-px-2 jF-pt-6 jF-pb-2 sm:jF-px-2"
					onKeyDown={handleKeyDown}
					onSubmit={(e) => e.preventDefault()}
				>
					{/* Loading Overlay */}
					{sendMail.isPending && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="jF-absolute jF-w-full jF-h-full jF-top-0 jF-left-0 jF-bg-slate-200 jF-bg-opacity-75 jF-z-50"
						>
							<div className="jF-flex jF-justify-center jF-items-center jF-h-full jF-w-full">
								<div role="status">
									<svg
										aria-hidden="true"
										className="jF-w-12 jF-h-12 jF-text-opacity-0 jF-text-white jF-animate-spin jF-bg-opacity-0 jF-fill-[--jF-theme-color]"
										viewBox="0 0 100 101"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
											fill="currentColor"
										/>
										<path
											d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
											fill="currentFill"
										/>
									</svg>
									<span className="jF-sr-only">Loading...</span>
								</div>
							</div>
						</motion.div>
					)}

					<AnimatePresence initial={false} mode="wait" custom={isForwards}>
						{selectedIndex === 0 && (
							<motion.div
								key={0}
								initial="initial"
								animate="animate"
								exit="exit"
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								variants={variants}
								custom={isForwards}
								className="jF-space-y-4"
							>
								<FormHeading />
								<div className="jF-space-y-2 jF-gap-x-2 sm:jF-flex sm:jF-gap-3 sm:jF-space-y-0">
									<div className="jF-flex-1">
										<div className="jF-relative jF-mt-2">
											<input
												name="firstName"
												value={firstName}
												onChange={handleFirstNameChange}
												aria-label="First Name"
												type="text"
												id="firstName"
												placeholder="First Name"
												maxLength={30}
												autoComplete="given-name"
												onFocus={() => handleFocus("firstName")}
												className={clsx(
													errors.firstName
														? "jF-ring-red-500 focus:jF-ring-red-500"
														: "jF-ring-gray-300 focus:jF-ring-gray-900",
													"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-[--ct-primary] jF-ring-inset focus:jF-ring-1 focus:jF-ring-inset jF-pl-3 placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6",
												)}
											/>
										</div>
										{errors.firstName && (
											<span className="jF-text-xs jF-text-red-500">
												First Name Required
											</span>
										)}
									</div>
									<div className="jF-flex-1">
										<div className="jF-relative jF-mt-2">
											<input
												name="lastName"
												value={lastName}
												onChange={handleLastNameChange}
												aria-label="Last Name"
												type="text"
												id="lastName"
												placeholder="Last Name"
												maxLength={30}
												autoComplete="family-name"
												onFocus={() => handleFocus("lastName")}
												className={clsx(
													errors.lastName
														? "jF-ring-red-500 focus:jF-ring-red-500"
														: "jF-ring-gray-300 focus:jF-ring-gray-900",
													"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-[--ct-primary] jF-ring-inset focus:jF-ring-1 focus:jF-ring-inset jF-pl-3 placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6",
												)}
											/>
										</div>
										{errors.lastName && (
											<span className="jF-text-xs jF-text-red-500">
												Last Name Required
											</span>
										)}
									</div>
								</div>
								<div className="jF-space-y-2 jF-gap-x-2 sm:jF-flex sm:jF-gap-3 sm:jF-space-y-0">
									<div className="jF-relative jF-flex-1">
										<div className="jF-relative jF-flex">
											<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-left-0 jF-flex jF-items-center jF-pl-3">
												<EnvelopeIcon
													className="jF-h-5 jF-w-5 jF-text-gray-400"
													aria-hidden="true"
												/>
											</div>
											<input
												value={email}
												onChange={handleEmailChange}
												aria-label="Email Address"
												type="email"
												id="email"
												placeholder="Email Address"
												maxLength={50}
												autoComplete="email"
												onFocus={() => handleFocus("email")}
												className={clsx(
													errors.email
														? "jF-ring-red-500 focus:jF-ring-red-500"
														: "jF-ring-gray-300 focus:jF-ring-gray-900",
													"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-pl-10 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-[--ct-primary] jF-ring-inset focus:jF-ring-1 focus:jF-ring-inset placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6 jF-bg-none",
												)}
											/>
										</div>
										{errors.email && emailMessage && (
											<span className="jF-text-xs jF-text-red-500">
												{emailMessage}
											</span>
										)}
									</div>
									<div className="jF-relative jF-flex-1">
										{!isPhoneVerified ? (
											<>
												<PhoneValidation />
											</>
										) : (
											<>
												<div className="jF-flex-1 jF-flex jF-items-center jF-border jF-border-green-500 jF-bg-green-50 jF-p-2 jF-rounded-md">
													<input
														defaultValue={phone}
														className="jF-bg-transparent !jF-outline-none !jF-border-none jF-flex-1"
														readOnly
													/>
													<CheckIcon className="jF-w-4 jF-h-4 max-md:jF-w-5 max-md:jF-h-5 jF-text-green-500 jF-ml-2" />
												</div>
											</>
										)}
									</div>
								</div>

								<div className="jF-space-y-4 sm:jF-flex sm:jF-gap-3 sm:jF-space-y-0">
									<div className="jF-flex-1">
										<textarea
											value={enquiry}
											onChange={handleEnquiryChange}
											name="enquiry"
											id="enquiry"
											placeholder="Message"
											rows={4}
											maxLength={150}
											className={clsx(
												errors.enquiry
													? "jF-ring-red-500 focus:jF-ring-red-500"
													: "jF-ring-gray-300 focus:jF-ring-gray-900",
												"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-px-3.5 jF-py-2 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-[--ct-primary] jF-ring-inset focus:jF-ring-1 focus:jF-ring-inset placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6 jF-bg-none",
											)}
										/>
										{errors.enquiry && (
											<span className="jF-text-xs jF-text-red-500">
												This field is required
											</span>
										)}
									</div>
								</div>

								{/* Terms and Conditions */}
								<div id="tc-toggle" className={clsx("!jF-mt-4")} tabIndex={-1}>
									<ToggleSwitch
										label=""
										enabled={enabledTC}
										setEnabled={setEnabledTC}
									/>
									{errors.tc && (
										<span className="jF-block jF-mt-1 jF-text-xs jF-text-red-500">
											Please agree to the terms and conditions to continue
										</span>
									)}
								</div>
							</motion.div>
						)}

						{/* Success Page */}
						{selectedIndex === stepsMaxIndex && (
							<motion.div
								key={stepsMaxIndex}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="space-y-4"
							>
								<FormSuccessHeading />
							</motion.div>
						)}
					</AnimatePresence>
				</form>
			</AnimateChangeInHeight>

			{/* Navigation */}
			{selectedIndex < stepsMaxIndex ? (
				<div className="jF-flex jF-items-center jF-flex-wrap jF-justify-end jF-border-t jF-border-gray-200 jF-bg-white jF-mt-2 jF-px-4 jF-py-3 sm:jF-px-6 max-sm:jF-justify-center sm:jF-flex-nowrap">
					<div className="jF-flex jF-flex-1 jF-justify-end jF-mt-2 jF-flex-nowrap max-sm:jF-mt-0 max-sm:jF-justify-center md:jF-flex-nowrap max-sm:jF-flex-wrap">
						<button
							className={clsx(
								!toggleTC
									? "jF-text-gray-500 jF-bg-gray-100 hover:jF-cursor-not-allowed jF-ring-gray-100 jF-basis-auto jF-w-auto"
									: "jF-bg-[--ct-primary] hover:jF-bg-[--ct-primary-darker] jF-text-white",
								"jF-basis-2/4 jF-justify-center jF-w-auto jF-relative jF-ml-3 jF-inline-flex jF-items-center jF-rounded-md jF-px-3 jF-py-2 jF-text-md jF-font-semibold md:jF-basis-auto sm:jF-w-auto sm:jF-basis-full",
							)}
							onClick={(e) => {
								e.preventDefault();
								submit();
							}}
						>
							Submit
						</button>
					</div>
				</div>
			) : (
				<>
					<div className="jF-flex jF-items-center jF-flex-wrap jF-justify-center jF-border-t jF-border-gray-200 jF-bg-white jF-mt-2 jF-px-4 jF-py-3 max-sm:jF-px-6">
						<button
							className="jF-bg-[--ct-primary] hover:jF-bg-[--ct-primary-darker] jF-relative jF-mx-auto jF-block jF-text-center jF-w-1/3 jF-rounded-md jF-px-3 jF-py-2 jF-text-md jF-font-semibold jF-text-white"
							onClick={() => {
								close();
							}}
						>
							Close
						</button>
					</div>
				</>
			)}
		</div>
	);
}

function FormHeading({ className }: { className?: string }) {
	return (
		<>
			<div className={clsx(className, "")}>
				<div className="jF-mt-3 jF-text-center sm:jF-mt-5">
					<h2 className="jF-text-[2.2rem] jF-mb-3 jF-font-semibold jF-leading-8 jF-text-[--ct-secondary]">
						General Enquiry Form
					</h2>
				</div>
			</div>
		</>
	);
}

function FormSuccessHeading({ className }: { className?: string }) {
	return (
		<>
			<div className={clsx(className, "")}>
				<div className="jF-mx-auto jF-flex jF-h-20 jF-w-20 jF-items-center jF-justify-center jF-rounded-full jF-bg-[--ct-bullet-bg]">
					<CheckIcon
						className="jF-h-10 jF-w-10 jF-text-[--ct-primary]"
						aria-hidden="true"
					/>
				</div>
				<div className="jF-mt-3 jF-text-center sm:jF-mt-5">
					<h3 className="jF-text-[2rem] jF-mb-3 jF-font-semibold jF-leading-8 jF-text-[--ct-secondary]">
						Enquiry Received
					</h3>
					<div className="jF-mt-5 jF-mb-5">
						<p className="jF-text-md jF-text-gray-500">
							Thanks for contacting us! We will get in touch with you shortly.
						</p>
					</div>
				</div>
			</div>
		</>
	);
}
